// Offline topics bundle loader for the Bible-search arc, Plan 04
// (Layer 3). This module is the boundary the search UI reads topics
// through, parallel to rule #65's facade discipline for BSB data: the
// UI never fetches /bible-search/topics.json directly nor imports
// topic-search.ts itself, it goes through here.
//
// The bundle (public/bible-search/topics.json) is built offline by
// scripts/build-bible-search-topics.mjs from the curated taxonomy
// (rule #4 cross-boundary: the xlsx / Supabase taxonomy is the source
// of truth, this static artifact is the offline duplicate). It carries
// verse REFS only; verse TEXT resolves from the bundled BSB at render
// via resolveTopicVerseText (rule #62/#76: BSB is the single text
// source). en-US content only at v1 (rule #7); search chrome is i18n'd
// separately.

import { getVerse } from '@/lib/bible/active';
import type { BsbVerse } from '@/lib/bible/active';
import {
  searchTopics,
  searchTopicsWithStrength,
  type TopicSearchRecord,
  type TopicMatchStrength,
} from '@/lib/topic-search';

// Must match BUNDLE_VERSION in scripts/build-bible-search-topics.mjs.
// A future shape/content change bumps both; the loader rejects a bundle
// whose version it does not understand so the topic layer fails closed
// (the verse + reference layers keep working) rather than mis-rendering.
const SUPPORTED_BUNDLE_VERSION = 1;

const BUNDLE_URL = '/bible-search/topics.json';

export interface BundledTopicVerse {
  /** BSB display name, e.g. "Philippians" / "Psalm". */
  book: string;
  /** BSB slug for navigation + text resolution, e.g. "philippians". */
  bookSlug: string;
  chapter: number;
  verseStart: number;
  verseEnd: number;
  /** Curated citation, e.g. "Philippians 4:6-7". */
  referenceText: string;
}

// Extends TopicSearchRecord (slug + name + description) so searchTopics
// runs over it directly, plus the bundle-only fields.
export interface BundledTopic extends TopicSearchRecord {
  displayOrder: number;
  verses: BundledTopicVerse[];
}

interface TopicsBundle {
  version: number;
  locale: string;
  topics: BundledTopic[];
}

let cache: Promise<BundledTopic[]> | null = null;
let resolved: BundledTopic[] | null = null;

function parseBundle(data: TopicsBundle): BundledTopic[] {
  if (data.version !== SUPPORTED_BUNDLE_VERSION) {
    throw new Error(
      `unsupported topics bundle version ${data.version} (expected ${SUPPORTED_BUNDLE_VERSION})`,
    );
  }
  if (!Array.isArray(data.topics)) {
    throw new Error('topics bundle missing topics array');
  }
  return data.topics;
}

async function fetchBundle(): Promise<BundledTopic[]> {
  const res = await fetch(BUNDLE_URL);
  if (!res.ok) throw new Error(`topics bundle fetch failed: ${res.status}`);
  return parseBundle(await res.json() as TopicsBundle);
}

/**
 * Lazy, module-cached topics load (mirrors bsb-search.ts: cache the
 * Promise, clear it on rejection so a transient failure does not poison
 * every subsequent call). The bundle is fetched once on first
 * topic-eligible search and served from the service worker cache
 * thereafter.
 */
export async function loadTopics({ localOnly = false }: { localOnly?: boolean } = {}): Promise<BundledTopic[]> {
  // Public search stays available before owner verification. Read the SW cache
  // directly: fetch() may start a network request even on an offline cold open.
  if (localOnly) {
    if (resolved) return resolved;
    const response = typeof caches === 'undefined' ? undefined : await caches.match(BUNDLE_URL);
    return response ? parseBundle(await response.json() as TopicsBundle) : [];
  }
  if (!cache) {
    cache = fetchBundle().then((data) => { resolved = data; return data; }).catch((err) => {
      cache = null;
      throw err;
    });
  }
  return cache;
}

/** Test/recovery hook: drop the cached bundle so the next load refetches. */
export function clearTopicsCache(): void {
  cache = null;
  resolved = null;
}

// Re-export the matchers so the UI gets topics + the search over them from
// one module boundary. Both are generic over TopicSearchRecord, so passing
// BundledTopic[] returns BundledTopic[] (keeping verses + slug).
// searchTopicsWithStrength (08.4) additionally tags each result with its
// match-strength band, which the Top-preview ordering reads.
export { searchTopics, searchTopicsWithStrength };
export type { TopicMatchStrength };

function flattenVerseText(t: BsbVerse['t']): string {
  if (typeof t === 'string') return t;
  return t.map((seg) => seg.text).join('');
}

/**
 * Resolve a bundled verse ref to its BSB text at render time (refs-only
 * bundle, BSB single source). Walks verseStart..verseEnd via the shared
 * getVerse resolver (which aggregates vCont fragments per verse), skips
 * omitted verses, and joins with a single space. Returns '' if nothing
 * resolves (caller falls back to the reference label alone).
 */
export async function resolveTopicVerseText(
  verse: Pick<BundledTopicVerse, 'bookSlug' | 'chapter' | 'verseStart' | 'verseEnd'>,
): Promise<string> {
  const parts: string[] = [];
  for (let n = verse.verseStart; n <= verse.verseEnd; n++) {
    const resolved = await getVerse(verse.bookSlug, verse.chapter, n);
    if (resolved && resolved.omitted !== true) {
      const text = flattenVerseText(resolved.t).trim();
      if (text) parts.push(text);
    }
  }
  return parts.join(' ').trim();
}
